import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PractiseBookPageActions } from '../practise/practise.actions';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { BookService } from '../../book.service';
import { BookProgressActions } from './book-progress.actions';
import { BookFacade } from '../book/book.facade';
import { isDefined } from '@lyceo/shared/util';
import { ParagraphFacade } from '../paragraph/paragraph.facade';
import { AuthFacade } from '@lyceo/shared/auth/data-access';
import { AlfredModalActions } from '../exercise/exercise.actions';
import { BookProgressFacade } from './book-progress.facade';

@Injectable()
export class BookProgressEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly bookFacade: BookFacade = inject(BookFacade);
  private readonly paragraphFacade: ParagraphFacade = inject(ParagraphFacade);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly bookService: BookService = inject(BookService);
  private readonly bookProgressFacade: BookProgressFacade =
    inject(BookProgressFacade);

  public bookProgressLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.init),
      filter(() => !this.bookProgressFacade.paramBookProgressLoaded()),
      map(() => BookProgressActions.getBookProgress()),
    );
  });

  public getBookProgress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        BookProgressActions.getBookProgress,
        AlfredModalActions.sessionFinished,
      ),
      map(() => this.bookFacade.paramBookId()),
      filter(isDefined),
      exhaustMap((bookId) =>
        this.bookService.getBookProgress(bookId).pipe(
          map((bookProgressResponse) =>
            BookProgressActions.getBookProgressSuccess({
              bookProgressResponse: bookProgressResponse,
            }),
          ),
          catchError((error: Error) =>
            of(BookProgressActions.getBookProgressFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getBookProgressFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookProgressActions.getBookProgressFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het laden van de voortgang is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public updateParagraphTheory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.readTheoryClicked),
      exhaustMap((action) => {
        const bookId: string | undefined = this.bookFacade.paramBookId();
        const paragraphId: string | undefined =
          this.paragraphFacade.paramParagraphId();
        const personId: string | undefined = this.authFacade.userId();

        if (
          !isDefined(bookId) ||
          !isDefined(paragraphId) ||
          !isDefined(personId)
        ) {
          return of(
            BookProgressActions.updateBookProgressFailure({
              error: new Error(
                'Missing required data for book progress update',
              ),
            }),
          );
        }

        return this.bookService
          .patchBookProgress(bookId, {
            paragraphId,
            personId,
            readTheory: action.readTheory,
          })
          .pipe(
            map((bookProgressResponse) =>
              BookProgressActions.updateBookProgressSuccess({
                bookProgressResponse: bookProgressResponse,
              }),
            ),
            catchError((error: Error) =>
              of(BookProgressActions.updateBookProgressFailure({ error })),
            ),
          );
      }),
    );
  });

  public updateParagraphVideoWatched$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.videoWatched),
      exhaustMap((action) => {
        const bookId: string | undefined = this.bookFacade.paramBookId();
        const paragraphId: string | undefined =
          this.paragraphFacade.paramParagraphId();
        const personId: string | undefined = this.authFacade.userId();

        if (
          !isDefined(bookId) ||
          !isDefined(paragraphId) ||
          !isDefined(personId)
        ) {
          return of(
            BookProgressActions.updateBookProgressFailure({
              error: new Error(
                'Missing required data for book progress update',
              ),
            }),
          );
        }

        return this.bookService
          .patchBookProgress(bookId, {
            paragraphId,
            personId,
            seenVideos: [action.video.id],
          })
          .pipe(
            map((bookProgressResponse) =>
              BookProgressActions.updateBookProgressSuccess({
                bookProgressResponse: bookProgressResponse,
              }),
            ),
            catchError((error: Error) =>
              of(BookProgressActions.updateBookProgressFailure({ error })),
            ),
          );
      }),
    );
  });

  public updateBookProgressSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookProgressActions.updateBookProgressSuccess),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'success',
            message: 'Jouw voortgang voor dit onderwerp is bijgewerkt!',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public updateBookProgressFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookProgressActions.updateBookProgressFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het opslaan van de voortgang is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );
}
